<template>
  <div class="news-page py-8 px-4">
    <div class="max-w-7xl mx-auto">
      <!-- 页面标题 -->
      <div class="text-center mb-12">
        <h1 class="text-5xl md:text-6xl font-orbitron font-bold mb-4">
          <span class="text-gradient">Gaming</span> News
        </h1>
        <p class="text-cyber-light/70 text-lg max-w-2xl mx-auto">
          Stay updated with the latest game releases, in-depth reviews, developer interviews, gaming industry insights, and community highlights. Discover new games, read expert analysis, and join the conversation about the future of browser gaming.
        </p>
      </div>

      <!-- 特色游戏 -->
      <div class="cyber-card mb-12" v-if="featuredGame">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div class="relative w-full h-64 rounded-lg overflow-hidden">
            <img 
              :src="featuredGame.thumb6 || featuredGame.thumb5 || featuredGame.thumb4" 
              :alt="featuredGame.name"
              class="w-full h-full object-cover"
              @error="handleImageError"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-cyber-dark/80 to-transparent"></div>
          </div>
          <div>
            <div class="flex items-center text-sm text-cyber-light/50 mb-4">
              <span class="cyber-badge">Featured</span>
              <span class="mx-2">•</span>
              <span>{{ featuredGame.category }}</span>
              <span class="mx-2">•</span>
              <span>{{ formatDate(featuredGame.create_date) }}</span>
            </div>
            <h2 class="text-3xl font-orbitron font-bold mb-4 text-cyber-light">
              {{ featuredGame.name }}
            </h2>
            <p class="text-cyber-light/70 mb-6 line-clamp-4">
              {{ featuredGame.description }}
            </p>
            <div class="flex gap-4">
              <button class="cyber-button" @click="viewGame(featuredGame)">
                View Details
              </button>
              <button class="cyber-button-outline" @click="playGame(featuredGame)">
                Play Now
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 游戏分类标签 -->
      <div class="flex flex-wrap gap-2 mb-8">
        <button 
          v-for="category in gameCategories" 
          :key="category"
          :class="[
            'px-4 py-2 rounded-lg font-medium transition-all duration-300',
            selectedCategory === category 
              ? 'bg-cyber-blue text-cyber-dark' 
              : 'bg-cyber-gray/50 text-cyber-light hover:bg-cyber-blue/20'
          ]"
          @click="selectedCategory = category"
        >
          {{ category }}
        </button>
      </div>

      <!-- 游戏新闻列表 -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <article v-for="game in filteredGames" :key="game.name" class="cyber-card group cursor-pointer" @click="viewGame(game)">
          <div class="relative w-full h-48 rounded-lg overflow-hidden mb-4">
            <img 
              :src="game.thumb6 || game.thumb5 || game.thumb4" 
              :alt="game.name"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              @error="handleImageError"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-cyber-dark/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div class="absolute bottom-2 left-2">
              <span class="cyber-badge-purple text-xs">{{ game.category }}</span>
            </div>
          </div>
          <div class="flex items-center text-xs text-cyber-light/50 mb-2">
            <span>{{ formatDate(game.create_date) }}</span>
            <span class="mx-2">•</span>
            <span>{{ game.category }}</span>
          </div>
          <h3 class="text-xl font-orbitron font-bold mb-3 text-cyber-light group-hover:text-cyber-blue transition-colors">
            {{ game.name }}
          </h3>
          <p class="text-cyber-light/70 text-sm mb-4 line-clamp-3">{{ game.description }}</p>
          <div class="flex items-center justify-between">
            <button class="text-cyber-blue hover:text-cyber-light transition-colors text-sm font-medium">
              View Details →
            </button>
            <button 
              class="cyber-button-outline text-xs px-3 py-1"
              @click.stop="playGame(game)"
            >
              Play
            </button>
          </div>
        </article>
      </div>

      <!-- 分页 -->
      <div class="flex justify-center mt-12" v-if="totalPages > 1">
        <nav class="flex items-center space-x-2">
          <button 
            class="cyber-button px-3 py-2" 
            :disabled="currentPage === 1"
            @click="currentPage = Math.max(1, currentPage - 1)"
          >
            Previous
          </button>
          <button 
            v-for="page in visiblePages" 
            :key="page"
            :class="[
              'cyber-button px-3 py-2',
              currentPage === page ? 'bg-cyber-blue text-cyber-dark' : ''
            ]"
            @click="currentPage = page"
          >
            {{ page }}
          </button>
          <button 
            class="cyber-button px-3 py-2" 
            :disabled="currentPage === totalPages"
            @click="currentPage = Math.min(totalPages, currentPage + 1)"
          >
            Next
          </button>
        </nav>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getGames } from '@/services/gameService'

const router = useRouter()
const selectedCategory = ref('All')
const currentPage = ref(1)
const gamesPerPage = 9
const games = ref([])
const loading = ref(true)

// 获取游戏数据
const fetchGames = async () => {
  try {
    loading.value = true
    const gameData = await getGames()
    games.value = gameData
  } catch (error) {
    console.error('Failed to fetch games:', error)
  } finally {
    loading.value = false
  }
}

// 特色游戏（最新发布的游戏）
const featuredGame = computed(() => {
  return games.value.length > 0 ? games.value[0] : null
})

// 游戏分类
const gameCategories = computed(() => {
  const categories = ['All', ...new Set(games.value.map(game => game.category))]
  return categories
})

// 过滤游戏
const filteredGames = computed(() => {
  let filtered = games.value
  
  if (selectedCategory.value !== 'All') {
    filtered = filtered.filter(game => game.category === selectedCategory.value)
  }
  
  // 分页
  const startIndex = (currentPage.value - 1) * gamesPerPage
  const endIndex = startIndex + gamesPerPage
  return filtered.slice(startIndex, endIndex)
})

// 总页数
const totalPages = computed(() => {
  const filtered = selectedCategory.value === 'All' 
    ? games.value 
    : games.value.filter(game => game.category === selectedCategory.value)
  return Math.ceil(filtered.length / gamesPerPage)
})

// 可见页码
const visiblePages = computed(() => {
  const pages = []
  const maxVisible = 5
  const start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  const end = Math.min(totalPages.value, start + maxVisible - 1)
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// 格式化日期
const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// 查看游戏详情
const viewGame = (game) => {
  const encodedName = encodeURIComponent(game.name)
  router.push(`/game/${encodedName}`)
}

// 直接玩游戏
const playGame = (game) => {
  if (game.url) {
    window.open(game.url, '_blank')
  }
}

// 处理图片加载错误
const handleImageError = (event) => {
  event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMTAxMDEwIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2NjY2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5HYW1lIEltYWdlPC90ZXh0Pgo8L3N2Zz4K'
}

// 监听分类变化，重置页码
const watchCategory = computed(() => selectedCategory.value)
watchCategory.value && (currentPage.value = 1)

onMounted(() => {
  fetchGames()
})
</script> 